/**
 * Program Categories Page
 *
 * Dashboard view of all program categories with filtering
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { programCategoryAPI, drugProgramAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge
} from '../components/ui'
import ProgramCategoryEditModal from '../components/ProgramCategoryEditModal'

export default function ProgramCategoriesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editModal, setEditModal] = useState({ isOpen: false, category: null })

  // Get auth state to check clinic selection
  const { user, currentClinicId } = useAuthStore()
  const { getChangesByType } = useDraftStore()

  // Search state
  const [search, setSearch] = useState(searchParams.get('search') || '')

  // Check if user has access
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  // Helper function to merge drafts with database categories
  const mergeCategoriesWithDrafts = (dbCategories) => {
    const draftChanges = getChangesByType('program_category')

    // Start with database categories
    let mergedCategories = [...dbCategories]

    // Apply draft changes
    draftChanges.forEach(change => {
      if (change.operation === 'create') {
        // Add new categories from drafts with a draft indicator
        mergedCategories.push({
          ...change.new_data,
          _isDraft: true,
          _draftChangeId: change.id
        })
      } else if (change.operation === 'update') {
        // Update existing categories
        const index = mergedCategories.findIndex(c =>
          c.category_key === change.resource_id || c._id === change.resource_id
        )
        if (index !== -1) {
          mergedCategories[index] = {
            ...mergedCategories[index],
            ...change.new_data,
            _isDraft: true,
            _draftChangeId: change.id
          }
        }
      }
    })

    return mergedCategories
  }

  // Fetch program categories
  const fetchCategories = async () => {
    if (!hasClinicAccess) {
      setError('Please select a clinic to view program categories')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await programCategoryAPI.list()

      if (response.data.success) {
        let categoriesList = response.data.data || []

        // Apply search filter locally
        if (search) {
          const searchLower = search.toLowerCase()
          categoriesList = categoriesList.filter(cat =>
            cat.display_name?.toLowerCase().includes(searchLower) ||
            cat.category_key?.toLowerCase().includes(searchLower)
          )
        }

        // Merge with draft changes
        const mergedCategories = mergeCategoriesWithDrafts(categoriesList)
        setCategories(mergedCategories)
      } else {
        throw new Error(response.data.message || 'Failed to fetch program categories')
      }
    } catch (err) {
      console.error('Failed to fetch program categories:', err)
      setError(apiHelpers.handleError(err).message)
    } finally {
      setLoading(false)
    }
  }

  // Modal handlers
  const handleEditCategory = (category) => {
    setEditModal({ isOpen: true, category })
  }

  const closeEditModal = () => {
    setEditModal({ isOpen: false, category: null })
  }

  const handleCategorySave = () => {
    closeEditModal()
    fetchCategories() // Refresh list
  }

  // Update URL params when search changes
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    setSearchParams(params)
  }, [search, setSearchParams])

  // Fetch data when filters change
  useEffect(() => {
    fetchCategories()
  }, [search, currentClinicId])

  // Handle search input
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
  }

  // Calculate stats from categories
  const stats = {
    total: categories.length,
    withPrograms: categories.filter(c => c.program_definition_ids?.length > 0).length,
    multiConcurrent: categories.filter(c => c.allow_multiple_concurrent).length
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view program categories.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Program Categories</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
                Manage program categories that group multiple drug programs
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                onClick={() => setEditModal({ isOpen: true, category: null })}
                className="text-sm"
              >
                Add Category
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">With Programs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.withPrograms}</div>
              <p className="text-xs text-muted-foreground">
                Categories with linked programs
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Multi-Concurrent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.multiConcurrent}</div>
              <p className="text-xs text-muted-foreground">
                Allow multiple enrollments
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Search Filter */}
      <div className="container mx-auto px-4 pb-6">
        <Card>
          <CardContent className="p-6">
            <div className="max-w-md">
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Search by name or key..."
                value={search}
                onChange={handleSearchChange}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-pulse text-muted-foreground">Loading program categories...</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-destructive">
                <p>Error loading program categories: {error}</p>
                <Button
                  variant="outline"
                  onClick={fetchCategories}
                  className="mt-4"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Categories List */}
      {!loading && !error && categories && categories.length > 0 && (
        <div className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {categories.map((category) => (
              <Card
                key={category._id || category.category_key || category._draftChangeId}
                className={`hover:shadow-md transition-shadow ${category._isDraft ? 'border-amber-500 border-2' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="space-y-2">
                    {/* Badges row */}
                    <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                      {category._isDraft && (
                        <Badge className="bg-amber-500 text-white text-xs">DRAFT</Badge>
                      )}
                      {category.allow_multiple_concurrent && (
                        <Badge variant="secondary" className="text-xs">Multi-Concurrent</Badge>
                      )}
                    </div>
                    {/* Title and description */}
                    <div>
                      <CardTitle className="text-lg leading-tight">{category.display_name}</CardTitle>
                      <CardDescription className="mt-1 text-xs">
                        {category.category_key}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Linked Programs:</span>
                      <span className="font-semibold">{category.program_definition_ids?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Required Metrics:</span>
                      <span>{category.required_metrics?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Marketing Sections:</span>
                      <span>{Object.keys(category.marketing_copy || {}).length}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/program-categories/${category._id || category.category_key}`)}
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditCategory(category)}
                    >
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && (!categories || categories.length === 0) && (
        <div className="container mx-auto px-4 pb-8">
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <p className="text-muted-foreground mb-4">No program categories found.</p>
                {search && (
                  <Button variant="outline" onClick={() => setSearch('')}>
                    Clear Search
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      <ProgramCategoryEditModal
        category={editModal.category}
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        onSave={handleCategorySave}
      />
    </div>
  )
}
